import { z } from 'zod'
import { createdAtSortQueryShape, creatorListQueryShape } from '../list-query/contract.ts'
import { accountDisplaySchema } from '../profile/contract.ts'
import { tagIdsSchema as customerTagIdsSchema, tagOptionSchema as customerTagOptionSchema } from '../tag/contract.ts'

export {
  DEFAULT_TAG_COLOR as CUSTOMER_DEFAULT_TAG_COLOR,
  tagColorSchema as customerTagColorSchema,
  tagUpsertSchema as customerTagUpsertSchema,
  tagOptionSchema as customerTagOptionSchema,
  tagUpsertRequestSchema as customerTagUpsertRequestSchema,
  tagRemoveSchema as customerTagRemoveSchema,
  tagBatchRemoveSchema as customerTagBatchRemoveSchema,
  tagListSchema as customerTagListSchema,
  tagSchema as customerTagSchema,
  tagListOutputSchema as customerTagListOutputSchema,
  tagOptionsInputSchema as customerTagOptionsInputSchema,
  type TagOptionsInput as CustomerTagOptionsInput,
  type TagListInput as CustomerTagListInput,
  type TagUpsertInput as CustomerTagUpsertInput,
  type TagRemoveInput as CustomerTagRemoveInput,
  type TagBatchRemoveInput as CustomerTagBatchRemoveInput
} from '../tag/contract.ts'

export const customerListSchema = z.object({
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  id: z.number().int().positive().optional(),
  keyword: z.string().trim().max(255).optional(),
  noteKeyword: z.string().trim().max(1000).optional(),
  tagIds: z.array(z.number().int().positive()).max(50).optional(),
  salespersonId: z.number().int().positive().optional(),
  status: z.enum(['enabled', 'disabled']).optional(),
  wecomBindingStatus: z.enum(['bound', 'unbound']).optional(),
  collectionBillGroupingDay: z.number().int().min(1).max(28).optional(),
  ...creatorListQueryShape,
  ...createdAtSortQueryShape
})

export const customerTypeSchema = z.enum(['normal', 'agent'])

export const customerMonthlyBillingShape = {
  collectionBillGroupingFrom: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .nullable()
    .optional(),
  autoCollectionBillGrouping: z.boolean().default(false),
  autoSendCollectionBillGroup: z.boolean().optional(),
  collectionBillGroupingDay: z.number().int().min(1).max(28).nullable().optional()
}

export const customerMonthlyBillingSchema = z
  .object(customerMonthlyBillingShape)
  .refine((value) => !value.autoCollectionBillGrouping || value.collectionBillGroupingDay != null, {
    message: '开启自动生成后请选择每月归组日',
    path: ['collectionBillGroupingDay']
  })
  .refine((value) => !value.autoSendCollectionBillGroup || value.autoCollectionBillGrouping, {
    message: '请先开启自动生成收款账单组',
    path: ['autoSendCollectionBillGroup']
  })

export const customerUpsertSchema = z.object({
  ...customerMonthlyBillingShape,
  // 普通资料编辑可省略账单设置，由服务端保留当前配置。
  autoCollectionBillGrouping: z.boolean().optional(),
  collectionBillGroupingDay: customerMonthlyBillingShape.collectionBillGroupingDay.optional(),
  name: z.string().trim().min(1, '客户名称不能为空').max(255),
  type: customerTypeSchema.default('normal'),
  wecomChatId: z.string().trim().max(255, '企微群 ID 最多 255 个字符').nullable().optional(),
  phone: z.string().trim().max(20).optional().nullable(),
  email: z.string().trim().email('邮箱格式不正确').max(255).optional().nullable(),
  note: z.string().trim().max(5000).optional().nullable(),
  companyInfo: z.string().trim().max(255).optional().nullable(),
  salespersonId: z.number().int().positive().nullish(),
  fileIds: z.array(z.number().int().positive()).default([]),
  tagIds: z.array(z.number().int().positive()).max(50).default([])
})

export const customerSetDisabledSchema = z.object({
  id: z.number().int().positive(),
  isDisabled: z.boolean()
})
const customerIdsSchema = z
  .array(z.number().int().positive())
  .min(1, '至少选择一个客户')
  .max(100, '单次最多处理 100 个客户')
export const customerBatchSetDisabledSchema = z.object({
  ids: customerIdsSchema,
  isDisabled: z.boolean()
})
export const customerBatchAddTagsSchema = z.object({
  ids: customerIdsSchema,
  tagIds: customerTagIdsSchema.max(50, '最多选择 50 个客户标签')
})
export const customerBatchClearTagsSchema = z.object({ ids: customerIdsSchema })
export type CustomerBatchClearTagsInput = z.infer<typeof customerBatchClearTagsSchema>
export type CustomerBatchAddTagsInput = z.infer<typeof customerBatchAddTagsSchema>

export const customerRemoveSchema = z.object({ id: z.number().int().positive() })
export const customerBatchMonthlyBillingSchema = customerMonthlyBillingSchema.safeExtend({ ids: customerIdsSchema })
export type CustomerBatchMonthlyBillingInput = z.infer<typeof customerBatchMonthlyBillingSchema>

export const customerBatchRemoveSchema = z.object({ ids: customerIdsSchema })

export const customerUpsertRequestSchema = z.object({
  id: z.number().int().positive().optional(),
  data: customerUpsertSchema
})

export const customerFileSchema = z.object({
  id: z.number().int().positive(),
  fileName: z.string(),
  originName: z.string(),
  fileType: z.string().nullable(),
  fileSize: z.string()
})

export const customerSchema = z.object({
  collectionBillGroupingFrom: z.string().nullable().optional(),
  autoCollectionBillGrouping: z.boolean(),
  autoSendCollectionBillGroup: z.boolean(),
  collectionBillGroupingDay: z.number().int().min(1).max(28).nullable(),
  id: z.number().int().positive(),
  name: z.string(),
  type: customerTypeSchema,
  wecomChatId: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  note: z.string().nullable(),
  companyInfo: z.string().nullable(),
  tags: z.array(customerTagOptionSchema),
  isDisable: z.boolean(),
  salesperson: z
    .object({
      id: z.number().int().positive(),
      name: z.string(),
      type: z.enum(['employee', 'agent']),
      isDisable: z.boolean()
    })
    .nullable(),
  attachments: z.array(
    z.object({
      id: z.number().int().positive(),
      file: customerFileSchema
    })
  ),
  creator: accountDisplaySchema,
  createdAt: z.date(),
  updatedAt: z.date()
})

export const customerListOutputSchema = z.object({
  data: z.array(customerSchema),
  total: z.number().int().nonnegative(),
  current: z.number().int().positive(),
  pageSize: z.number().int().positive()
})
export const customerOptionsInputSchema = z.object({ keyword: z.string().trim().max(255).optional() })
export const salespersonOptionSchema = z.object({
  label: z.string(),
  value: z.number().int().positive(),
  type: z.enum(['employee', 'agent']),
  isDisable: z.boolean()
})
export const customerOptionSchema = z.object({
  label: z.string(),
  value: z.number().int().positive(),
  type: customerTypeSchema,
  salesperson: salespersonOptionSchema.nullable()
})
export const customerBatchOperationResultSchema = z.object({ count: z.number().int().nonnegative() })
export const customerBatchRemoveFailureSchema = z.object({
  id: z.number().int().positive(),
  message: z.string()
})
export const customerBatchRemoveResultSchema = z.object({
  successCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  failures: z.array(customerBatchRemoveFailureSchema)
})

export type CustomerListInput = z.infer<typeof customerListSchema>
export type CustomerUpsertInput = z.input<typeof customerUpsertSchema>
export type CustomerSetDisabledInput = z.infer<typeof customerSetDisabledSchema>
export type CustomerBatchSetDisabledInput = z.infer<typeof customerBatchSetDisabledSchema>
export type CustomerRemoveInput = z.infer<typeof customerRemoveSchema>
export type CustomerBatchRemoveInput = z.infer<typeof customerBatchRemoveSchema>

export const CUSTOMER_SEARCH_BATCH_ASYNC_THRESHOLD = 200
export const customerSearchBatchActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('add-tags'), tagIds: customerTagIdsSchema.max(50) }),
  z.object({ action: z.literal('clear-tags') }),
  customerMonthlyBillingSchema.safeExtend({ action: z.literal('monthly-billing') }),
  z.object({ action: z.literal('enable') }),
  z.object({ action: z.literal('disable') }),
  z.object({ action: z.literal('delete') })
])
export type CustomerSearchBatchAction = z.infer<typeof customerSearchBatchActionSchema>
export const customerSearchBatchSchema = z.object({
  search: customerListSchema,
  expectedCount: z.number().int().positive(),
  operation: customerSearchBatchActionSchema
})
export type CustomerSearchBatchInput = z.infer<typeof customerSearchBatchSchema>
export const customerSearchBatchResultSchema = z.object({
  successCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  warnings: z.array(z.string())
})
export const customerSearchBatchOutputSchema = customerSearchBatchResultSchema.extend({
  count: z.number().int().nonnegative(),
  queued: z.boolean(),
  jobId: z.string().optional()
})
export const customerSearchBatchStatusInputSchema = z.object({ jobId: z.string().min(1) })
export const customerSearchBatchStatusSchema = customerSearchBatchResultSchema.extend({
  state: z.enum(['missing', 'waiting', 'active', 'completed', 'failed']),
  processed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative()
})
export type CustomerSearchBatchStatus = z.infer<typeof customerSearchBatchStatusSchema>

export const customerBillingCalendarOutputSchema = z.array(
  z.object({
    day: z.number().int().min(1).max(28).nullable(),
    count: z.number().int().nonnegative(),
    customers: z.array(z.object({ id: z.number().int().positive(), name: z.string() })).max(3)
  })
)
